import React, { useState } from "react";
import { useAuth } from "../../../context/Auth";
import Swal from "sweetalert2";
import { showApiErrors } from "../../../utils/helpers";
import { ArrowLeft, Building2 } from "lucide-react";

const NewSupplier = () => {
  const { requestWithAuth } = useAuth();
  const [newSupplierData, setNewSupplierData] = useState({});
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setNewSupplierData({ ...newSupplierData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newSupplierData.name?.trim()) {
      newErrors.name = "Supplier name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createSupplier = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setCreatingSupplier(true);
    const body = JSON.stringify(newSupplierData);
    try {
      await requestWithAuth("/supplier/", "POST", body, {
        "Content-Type": "application/json",
      });
      Swal.fire({
        title: "Success",
        icon: "success",
        text: "Supplier Created Successfully",
      }).then(() => {
        window.location.href = "/administration/suppliers";
      });
    } catch (error) {
      showApiErrors(error);
    } finally {
      setCreatingSupplier(false);
    }
  };

  const renderField = (label, name, type = "text", required = false) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        id={name}
        placeholder={`Enter ${label.toLowerCase()}`}
        onChange={handleChange}
        className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors[name]
          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
          : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
          }`}
      />
      {errors[name] && (
        <p className="mt-1.5 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Suppliers</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Company</span>
                <span>/</span>
                <span>Suppliers</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Create</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Supplier</h1>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Supplier Information</h2>
                <p className="text-sm text-gray-500">Enter the details for the new drug supplier</p>
              </div>
            </div>
          </div>

          <form onSubmit={createSupplier} className="p-6 space-y-6">
            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField("Supplier Name", "name", "text", true)}
                  {renderField("Supplier Address", "address", "text", false)}
                </div>
              </div>

              {/* Banking Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Banking Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField("Supplier Bank", "bank", "text", false)}
                  {renderField("Account Number", "account_number", "text", false)}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField("Contact Person", "contact_person", "text", false)}
                  {renderField("Contact Person Number", "contact_person_number", "tel", false)}
                </div>
              </div>
            </div>

            {/* Form Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={creatingSupplier}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                {!creatingSupplier ? (
                  <button
                    type="submit"
                    onClick={createSupplier}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                  >
                    Create Supplier
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="px-6 py-2.5 text-sm font-medium text-white bg-blue-400 rounded-lg cursor-not-allowed flex items-center gap-2"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900">Supplier Management</h3>
              <p className="mt-1 text-sm text-blue-700">
                Ensure all supplier information is accurate. This information will be used for orders, invoices, and payment processing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewSupplier;